import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import pl from "@locales/pl.json";
import en from "@locales/en.json";
import common from "@locales/common";
import monitoringTranslations from "@locales/monitoring";
import componentsTranslations from "@locales/components";
import taskTranslations from "@locales/tasks";
import programPreviewTranslations from "@locales/program_preview";
import programScheduleTranslations from "@locales/program_schedule";
import patientTranslations from "@locales/patient";
import integration from "@locales/integrations";
import onlinePayments from "@locales/online_payments";
import storeOrder from "@locales/store_order";
import storeClinic from "@locales/store_clinic";
import clinicGeneral from "@locales/clinic_general";
import empty_states from "@locales/empty_states";
import settings from "@locales/settings";
import cooperation_settings from "@locales/cooperation_settings";
import educationalMaterials from "@locales/educational_materials";
import autogenerate from "@locales/autogenerate_modal";
import sharedTranslations from "@locales/patient/shared";
import questionnairesTranslations from "@locales/questionnaires";
import chatTranslations from "@locales/chat";
import auth from "@locales/auth";
import validation from "@locales/validation";
import companySettingsTabs from "@locales/company_settings_tabs";
import diet from "@locales/diet";
import subscriptions from "@locales/subscriptions";
import schedule from "@locales/schedule";
import patientFiles from "@locales/patient_files";
import aiOnboarding from "@locales/ai_onboarding";
import aiNotes from "@locales/ai_notes";
import programPdf from "@locales/program_pdf";
import visitTemplates from "@locales/visit_templates";
import calendarEntries from "@locales/calendar_entries";
import calendarVisits from "@locales/calendar_visits";
import salesProducts from "@locales/sales_products";
import salesStorefront from "@locales/sales_storefront";
import publicStorefront from "@locales/public_storefront";
import publicBooking from "@locales/public_booking";
import storeSession from "@locales/store_session";
import publicBookingPicker from "@locales/public_booking_picker";
import salesPurchases from "@locales/sales_purchases";
import availability from "@locales/availability";
import navigation from "@locales/navigation";

import uiEn from "@UI/locales/en";
import uiPl from "@UI/locales/pl";

function deepMerge<T extends Record<string, unknown>>(
  a: T,
  b: Record<string, unknown>,
): T {
  const result = { ...a } as Record<string, unknown>;
  for (const key of Object.keys(b)) {
    if (
      result[key] &&
      typeof result[key] === "object" &&
      !Array.isArray(result[key]) &&
      typeof b[key] === "object" &&
      !Array.isArray(b[key])
    ) {
      result[key] = deepMerge(
        result[key] as Record<string, unknown>,
        b[key] as Record<string, unknown>,
      );
    } else {
      result[key] = b[key];
    }
  }
  return result as T;
}

i18next.use(initReactI18next).init({
  resources: {
    pl: {
      translation: {
        ...deepMerge(uiPl, pl as Record<string, unknown>),
        common: { ...uiPl.common, ...common.pl },
        diet: diet.pl,
        schedule: schedule.pl,
        monitoring: monitoringTranslations.pl,
        components: componentsTranslations.pl,
        company_settings_tabs: companySettingsTabs.pl,
        tasks: taskTranslations.pl,
        program_preview: programPreviewTranslations.pl,
        program_schedule: programScheduleTranslations.pl,
        patient: patientTranslations.pl,
        integrations: integration.pl,
        online_payments: onlinePayments.pl,
        store_order: storeOrder.pl,
        store_clinic: storeClinic.pl,
        clinic_general: clinicGeneral.pl,
        empty_states: empty_states.pl,
        settings: settings.pl,
        cooperation_settings: cooperation_settings.pl,
        educational_materials: educationalMaterials.pl,
        autogenerate_modal: autogenerate.pl,
        shared: sharedTranslations.pl,
        questionnaires: questionnairesTranslations.pl,
        chat: chatTranslations.pl,
        subscriptions: subscriptions.pl,
        ai_onboarding: aiOnboarding.pl,
        patient_files: patientFiles.pl,
        ai_notes: aiNotes.pl,
        program_pdf: programPdf.pl,
        visitTemplates: visitTemplates.pl,
        calendarEntries: calendarEntries.pl,
        calendarVisits: calendarVisits.pl,
        salesProducts: salesProducts.pl,
        salesStorefront: salesStorefront.pl,
        publicStorefront: publicStorefront.pl,
        publicBooking: publicBooking.pl,
        storeSession: storeSession.pl,
        publicBookingPicker: publicBookingPicker.pl,
        salesPurchases: salesPurchases.pl,
        availability: availability.pl,
        navigation: navigation.pl,
      },
      common: common.pl,
      auth: auth.pl,
      validation: validation.pl,
    },
    en: {
      translation: {
        ...deepMerge(uiEn, en as Record<string, unknown>),
        common: { ...uiEn.common, ...common.en },
        diet: diet.en,
        schedule: schedule.en,
        monitoring: monitoringTranslations.en,
        components: componentsTranslations.en,
        company_settings_tabs: companySettingsTabs.en,
        tasks: taskTranslations.en,
        program_preview: programPreviewTranslations.en,
        program_schedule: programScheduleTranslations.en,
        patient: patientTranslations.en,
        integrations: integration.en,
        online_payments: onlinePayments.en,
        store_order: storeOrder.en,
        store_clinic: storeClinic.en,
        clinic_general: clinicGeneral.en,
        empty_states: empty_states.en,
        settings: settings.en,
        cooperation_settings: cooperation_settings.en,
        educational_materials: educationalMaterials.en,
        autogenerate_modal: autogenerate.en,
        shared: sharedTranslations.en,
        questionnaires: questionnairesTranslations.en,
        chat: chatTranslations.en,
        subscriptions: subscriptions.en,
        ai_onboarding: aiOnboarding.en,
        patient_files: patientFiles.en,
        ai_notes: aiNotes.en,
        program_pdf: programPdf.en,
        visitTemplates: visitTemplates.en,
        calendarEntries: calendarEntries.en,
        calendarVisits: calendarVisits.en,
        salesProducts: salesProducts.en,
        salesStorefront: salesStorefront.en,
        publicStorefront: publicStorefront.en,
        publicBooking: publicBooking.en,
        storeSession: storeSession.en,
        publicBookingPicker: publicBookingPicker.en,
        salesPurchases: salesPurchases.en,
        availability: availability.en,
        navigation: navigation.en,
      },
      common: common.en,
      auth: auth.en,
      validation: validation.en,
    },
  },
  lng: "pl",
  fallbackLng: "pl",
  keySeparator: ".",
  debug: false,
  interpolation: {
    escapeValue: false,
  },
  defaultNS: "translation",
  ns: ["translation", "auth", "common", "validation"],
});

export default i18next;
